import {ChangeDetectionStrategy, Component, computed, Input, signal} from '@angular/core';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {ISelectOption} from '../../../shared-models/custom-select/custom-select';
import {ISharedSelectModalResult} from '../../../shared-models/modals/shared-select-modal';
import {SharedIconsModule} from '../../../../shared-modules/shared-icons/shared-icons.module';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {getDeepCopyOfObject} from '../../../shared-utils/object/object.utils';

@Component({
  selector: 'app-shared-select-modal',
  templateUrl: './shared-select-modal.component.html',
  styleUrls: ['./shared-select-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedIconsModule],
})
export class SharedSelectModalComponent {

  @Input() buttonAcceptText = 'Select';
  @Input() buttonCloseText = 'Cancel';
  @Input() modalTitle = 'Select';
  @Input() showSearchBar = false;

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;
  protected readonly allowMultipleSelection = signal(false);
  protected readonly allowSelectAllSelection = signal(false);
  protected readonly searchQuery = signal('');
  protected readonly selectOptions = signal<Record<string, ISelectOption>>({});
  protected readonly optionKeys = computed((): string[] => Object.keys(this.selectOptions()));
  protected readonly visibleOptionKeys = computed((): string[] => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.optionKeys();
    }
    return this.optionKeys().filter((key: string) =>
      (this.selectOptions()[key]?.name ?? '').toLowerCase().includes(query),
    );
  });
  protected readonly selectedKeys = computed((): string[] =>
    this.optionKeys().filter((key: string) => this.selectOptions()[key]?.selected && !this.selectOptions()[key]?.disabled),
  );
  protected readonly selectedKey = computed((): string | undefined => this.selectedKeys()[0]);
  protected readonly hasSelection = computed((): boolean => this.selectedKeys().length > 0);
  protected readonly allSelectableSelected = computed((): boolean => {
    const selectableKeys = this.optionKeys().filter((key: string) => !this.selectOptions()[key]?.disabled);
    return selectableKeys.length > 0 && selectableKeys.every((key: string) => this.selectOptions()[key]?.selected);
  });

  constructor(private readonly modalController: ModalControllerService) {
  }

  @Input()
  set options(value: Record<string, ISelectOption>) {
    this.selectOptions.set(getDeepCopyOfObject(value ?? {}));
  }

  @Input()
  set allowMultiple(value: boolean) {
    this.allowMultipleSelection.set(!!value);
  }

  @Input()
  set allowSelectAll(value: boolean) {
    this.allowSelectAllSelection.set(!!value);
  }

  /**
   * Filters the option list.
   */
  protected onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.searchQuery.set(value);
  }

  /**
   * Selects one option, or toggles it when the modal allows multiple stores.
   */
  protected onSelectOption(key: string): void {
    const option = this.selectOptions()[key];
    if (!option || option.disabled) {
      return;
    }
    if (this.allowMultipleSelection()) {
      this.selectOptions.update((current: Record<string, ISelectOption>) => ({
        ...current,
        [key]: {
          ...current[key],
          selected: !current[key].selected,
        },
      }));
      return;
    }
    this.selectOptions.update((current: Record<string, ISelectOption>) => {
      const next: Record<string, ISelectOption> = {};
      Object.keys(current).forEach((optionKey: string) => {
        next[optionKey] = {
          ...current[optionKey],
          selected: optionKey === key,
        };
      });
      return next;
    });
  }

  /**
   * Selects or clears every enabled option.
   */
  protected onToggleAll(): void {
    const shouldSelect = !this.allSelectableSelected();
    this.selectOptions.update((current: Record<string, ISelectOption>) => {
      const next: Record<string, ISelectOption> = {};
      Object.keys(current).forEach((optionKey: string) => {
        next[optionKey] = {
          ...current[optionKey],
          selected: current[optionKey].disabled ? false : shouldSelect,
        };
      });
      return next;
    });
  }

  /**
   * Closes the modal without applying a selection.
   */
  protected onCancel(): void {
    void this.modalController.dismiss({confirmed: false} as ISharedSelectModalResult);
  }

  /**
   * Closes the modal and returns the selected option key.
   */
  protected onConfirm(): void {
    const selectedKeys = this.selectedKeys();
    if (selectedKeys.length < 1) {
      return;
    }
    void this.modalController.dismiss({
      confirmed: true,
      selectedKey: selectedKeys.length === 1 ? selectedKeys[0] : undefined,
      selectedKeys,
    } as ISharedSelectModalResult);
  }
}
